import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiCheckSquare, FiEye, FiFolder, FiPlus, FiX } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { FadeIn } from '../components/motion';
import MoreMenu from '../components/MoreMenu';
import {
  EmptyState as ErpEmpty,
  ListState as ErpListState,
  LoadingSkeleton as ErpSkeleton,
  StatusBadge,
  formatCents as money,
} from '../components/shared/Ui';
import './Services.css';
import '../components/erp/Erp.css';
import { appAlert } from '../utils/appDialog';
import { PartyPicker } from '../components/commercial/CommercialUi';
import { invalidateProjects, queryKeys, staleTimes } from '../config/queryKeys';
import ListPager from '../components/ListPager';
import { useClientPager } from '../hooks/useClientPager';

const Projects = () => {
  const { hasPermission } = usePermissions();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', budget: '', customer_id: '' });
  const [taskTitle, setTaskTitle] = useState('');
  const [billingForm, setBillingForm] = useState({ service_company_id: '', rate: '' });

  const list = useQuery(queryKeys.erpProjects, async () => (await api.get('/projects')).data, {
    enabled: hasPermission('erp_projects_view') || hasPermission('tab_projects'),
  });
  const detail = useQuery(
    queryKeys.erpProject(selectedId),
    async () => (await api.get(`/projects/${selectedId}`)).data,
    { enabled: Boolean(selectedId) }
  );
  const serviceCompanies = useQuery(
    queryKeys.serviceCompanies,
    async () => (await api.get('/services/companies')).data,
    { staleTime: staleTimes.reference }
  );

  const create = useMutation(
    async () =>
      (
        await api.post('/projects', {
          name: form.name,
          code: form.code || null,
          customer_id: form.customer_id || null,
          budget_cents: Math.round(Number(form.budget || 0) * 100),
        })
      ).data,
    {
      onSuccess: (row) => {
        invalidateProjects(qc);
        setForm({ name: '', code: '', budget: '', customer_id: '' });
        setSelectedId(row.id);
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const addTask = useMutation(
    async () => (await api.post(`/projects/${selectedId}/tasks`, { title: taskTitle })).data,
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.erpProject(selectedId));
        setTaskTitle('');
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );
  const createBillingDraft = useMutation(
    async () =>
      (
        await api.post(`/projects/${selectedId}/billing-draft`, {
          service_company_id: Number(billingForm.service_company_id),
          rate_cents: Math.round(Number(billingForm.rate || 0) * 100),
        })
      ).data,
    {
      onSuccess: (result) => {
        qc.invalidateQueries(queryKeys.erpProject(selectedId));
        qc.invalidateQueries(queryKeys.salesDocuments('invoice'));
        void appAlert(
          `Draft invoice ${result.document?.number || ''} created from ${result.timesheet_count} timesheet entries. Review it in Sales; nothing was posted to GL.`
        );
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const rows = list.data || [];
  const {
    pageItems: pagedRows,
    pagination: projectPagination,
    setPage: setProjectPage,
  } = useClientPager(rows);
  const project = detail.data;
  const tasks = project?.tasks || [];
  const canManage = hasPermission('erp_projects_manage');
  const canBill = canManage && hasPermission('service_sales_manage');

  return (
    <div className="erp-page list-page">
      <FadeIn className="page-header">
        <div className="page-header-text">
          <h1><FiFolder aria-hidden /> Projects</h1>
          <p className="page-subtitle">
            Delivery projects, budgets, and tasks. Link timesheets via project_id (Recruiting → Timesheets).
          </p>
        </div>
      </FadeIn>

      {canManage && (
        <section className="erp-panel erp-panel--form">
          <div className="erp-panel-head">
            <h2>New project</h2>
            <p className="erp-panel-meta">Budgets are tracked against timesheet and bill activity.</p>
          </div>
          <div className="erp-form-grid">
            <div className="erp-field">
              <label htmlFor="project-name">Name</label>
              <input
                id="project-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Cloud migration"
              />
            </div>
            <div className="erp-field">
              <label htmlFor="project-code">Code</label>
              <input
                id="project-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="PRJ-001"
              />
            </div>
            <div className="erp-field">
              <PartyPicker
                id="project-customer"
                label="Customer"
                value={form.customer_id}
                onChange={(customerId) => setForm({ ...form, customer_id: customerId })}
              />
            </div>
            <div className="erp-field">
              <label htmlFor="project-budget">Budget</label>
              <input
                id="project-budget"
                type="number"
                step="0.01"
                inputMode="decimal"
                placeholder="0.00"
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
              />
            </div>
            <div className="erp-form-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => create.mutate()}
                disabled={!form.name || create.isLoading}
              >
                <FiPlus aria-hidden /> Create project
              </button>
            </div>
          </div>
        </section>
      )}

      <div className={`erp-record-layout${selectedId ? ' erp-detail-layout' : ''}`}>
        <section className="erp-panel">
          <div className="erp-panel-head">
            <h2>Projects</h2>
            <p className="erp-panel-meta">
              {rows.length} {rows.length === 1 ? 'project' : 'projects'}
            </p>
          </div>
          <ErpListState
            query={list}
            isEmpty={rows.length === 0}
            empty={
              <ErpEmpty
                icon={FiFolder}
                title="No projects yet"
                hint="Create a project to group tasks, budget and timesheet hours for a client engagement."
              />
            }
          >
            <div className="erp-table-scroll">
              <table className="erp-table">
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Name</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="erp-num">Budget</th>
                    <th scope="col" className="erp-cell-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((p) => (
                    <tr key={p.id} className={p.id === selectedId ? 'is-selected' : undefined}>
                      <td><span className="erp-code">{p.code || '—'}</span></td>
                      <td className="erp-cell-primary">{p.name}</td>
                      <td><StatusBadge status={p.status} /></td>
                      <td className="erp-num">{money(p.budget_cents, p.currency)}</td>
                      <td className="erp-cell-actions">
                        <MoreMenu
                          label={`Actions for ${p.name}`}
                          size="sm"
                          items={[
                            {
                              id: 'open',
                              label: 'Open',
                              icon: FiEye,
                              onSelect: () => setSelectedId(p.id),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ListPager
              page={projectPagination.page}
              pageCount={projectPagination.pageCount}
              total={projectPagination.total}
              limit={projectPagination.limit}
              onPageChange={setProjectPage}
            />
          </ErpListState>
        </section>

        {selectedId && (
          <section className="erp-panel">
            {detail.isLoading ? (
              <ErpSkeleton rows={3} label="Loading project" />
            ) : !project ? (
              <ErpEmpty icon={FiFolder} title="Project not found" hint="It may have been removed. Pick another project from the list." inline />
            ) : (
              <>
                <div className="erp-panel-head">
                  <h2>{project.name}</h2>
                  <div className="erp-panel-actions">
                    <StatusBadge status={project.status} />
                    <button
                      type="button"
                      className="btn btn-secondary btn-icon btn-icon-sm"
                      onClick={() => setSelectedId(null)}
                      title="Close project"
                      aria-label="Close project"
                    >
                      <FiX aria-hidden />
                    </button>
                  </div>
                </div>
                <p className="erp-panel-meta">
                  {project.code ? `${project.code} · ` : ''}
                  Budget {money(project.budget_cents, project.currency)} · {tasks.length}{' '}
                  {tasks.length === 1 ? 'task' : 'tasks'}
                </p>

                {canBill && (
                  <div className="erp-inline-form erp-stack-top">
                    <div className="erp-field">
                      <label htmlFor="project-billing-brand">Service brand</label>
                      <select
                        id="project-billing-brand"
                        value={billingForm.service_company_id}
                        onChange={(event) =>
                          setBillingForm({ ...billingForm, service_company_id: event.target.value })
                        }
                      >
                        <option value="">— Select mapped brand —</option>
                        {(serviceCompanies.data || []).map((company) => (
                          <option key={company.id} value={company.id}>{company.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="erp-field">
                      <label htmlFor="project-hourly-rate">Hourly billing rate</label>
                      <input
                        id="project-hourly-rate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={billingForm.rate}
                        onChange={(event) =>
                          setBillingForm({ ...billingForm, rate: event.target.value })
                        }
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => createBillingDraft.mutate()}
                      disabled={
                        !billingForm.service_company_id ||
                        !Number(billingForm.rate) ||
                        createBillingDraft.isLoading
                      }
                    >
                      Create draft time invoice
                    </button>
                  </div>
                )}

                {canManage && (
                  <div className="erp-inline-form erp-stack-top">
                    <div className="erp-field">
                      <label htmlFor="project-task-title">New task</label>
                      <input
                        id="project-task-title"
                        value={taskTitle}
                        onChange={(e) => setTaskTitle(e.target.value)}
                        placeholder="Draft migration plan"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => addTask.mutate()}
                      disabled={!taskTitle || addTask.isLoading}
                    >
                      <FiPlus aria-hidden /> Add task
                    </button>
                  </div>
                )}

                {tasks.length === 0 ? (
                  <ErpEmpty
                    icon={FiCheckSquare}
                    title="No tasks yet"
                    hint="Add the first deliverable so consultants can book time against it."
                    inline
                  />
                ) : (
                  <div className="erp-table-scroll erp-stack-top">
                    <table className="erp-table">
                      <thead>
                        <tr>
                          <th scope="col">Title</th>
                          <th scope="col">Status</th>
                          <th scope="col">Due</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tasks.map((t) => (
                          <tr key={t.id}>
                            <td className="erp-cell-primary">{t.title}</td>
                            <td><StatusBadge status={t.status} /></td>
                            <td>{t.due_date || <span className="erp-muted">—</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="erp-panel-head erp-stack-top">
                  <h3>Recent project time</h3>
                  <p className="erp-panel-meta">Approved billable entries become eligible for a draft invoice.</p>
                </div>
                {(project.recent_time || []).length === 0 ? (
                  <p className="erp-muted">No linked timesheets yet.</p>
                ) : (
                  <div className="erp-table-scroll">
                    <table className="erp-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Consultant</th>
                          <th className="erp-num">Hours</th>
                          <th>Billing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(project.recent_time || []).map((entry) => (
                          <tr key={entry.id}>
                            <td>{entry.date}</td>
                            <td>{[entry.user_first_name, entry.user_last_name].filter(Boolean).join(' ') || '—'}</td>
                            <td className="erp-num">{entry.hours}h</td>
                            <td><StatusBadge status={entry.billing_status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default Projects;
